import React from 'react';
import {
  UserPlus,
  UserCog,
  User,
  Briefcase,
  Building2,
  CalendarClock,
  GitBranch,
  Sparkles,
  Link2,
  Highlighter,
  MoreHorizontal,
  CircleDot,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import type { ITool } from '../../../core/tools/interfaces/ITool';
import type { ToolRegistry } from '../../../core/tools/ToolRegistry';
import { Logger } from '../../../utils/logger';
import { useChromeTools, type ChromeMount } from './useChromeTools';

const logger = Logger.getLoggerWithPrefix('ChromeMountPoint');

/** How many tools render inline before the rest fold into the overflow menu. */
const INLINE_LIMIT = 4;

/** Manifest `icon` string → lucide component, for tools without a custom toolbar component. */
const CHROME_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'user-plus': UserPlus,
  'user-cog': UserCog,
  user: User,
  briefcase: Briefcase,
  building: Building2,
  'calendar-clock': CalendarClock,
  'git-branch': GitBranch,
  sparkles: Sparkles,
  'link-2': Link2,
  highlighter: Highlighter,
};

interface ChromeMountPointProps {
  mount: ChromeMount;
  toolRegistry?: ToolRegistry;
  onToolExecute?: (toolId: string, params?: unknown) => void | Promise<void>;
  className?: string;
  /** Rendered before the cluster only when at least one tool is visible. */
  leading?: React.ReactNode;
  /** Rendered after the cluster only when at least one tool is visible
   *  (e.g. the divider separating this cluster from the next). */
  trailing?: React.ReactNode;
}

/**
 * Registry-driven canvas chrome mount (plan: emergent-canvas-chrome §6.4).
 * The only caller of useChromeTools. Renders each visible tool either via its
 * own getToolbarComponent or as a default icon button; execution always flows
 * UI → toolRegistry.executeTool → CommandManager (strict command flow).
 */
export const ChromeMountPoint: React.FC<ChromeMountPointProps> = ({
  mount,
  toolRegistry,
  onToolExecute,
  className,
  leading,
  trailing,
}) => {
  const { tools, context } = useChromeTools(toolRegistry, mount);

  if (!toolRegistry || !context || tools.length === 0) return null;

  const execute = async (tool: ITool) => {
    try {
      if (onToolExecute) {
        await onToolExecute(tool.manifest.id);
      } else {
        await toolRegistry.executeTool(tool.manifest.id);
      }
    } catch (error) {
      logger.error(`Failed to execute chrome tool ${tool.manifest.id}:`, error);
    }
  };

  const inline = tools.slice(0, INLINE_LIMIT);
  const overflow = tools.slice(INLINE_LIMIT);

  const renderIcon = (tool: ITool) => {
    const Icon = CHROME_ICONS[tool.manifest.icon] ?? CircleDot;
    return <Icon className="h-4 w-4" />;
  };

  const renderInlineTool = (tool: ITool, previous: ITool | undefined) => {
    const CustomComponent = tool.getToolbarComponent?.();
    const withDivider = previous !== undefined && previous.manifest.category !== tool.manifest.category;
    const isActive = tool.getActiveState?.(context) ?? false;

    return (
      <React.Fragment key={tool.manifest.id}>
        {withDivider && <div className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />}
        {CustomComponent ? (
          <CustomComponent tool={tool} context={context} onExecute={() => void execute(tool)} />
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid={`tid.editor.${mount}.${tool.manifest.id}`}
                aria-label={tool.manifest.name}
                aria-pressed={tool.getActiveState ? isActive : undefined}
                className={cn(
                  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg',
                  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                  isActive
                    ? 'bg-accent text-foreground'
                    : 'text-foreground/80 hover:bg-accent hover:text-foreground'
                )}
                onClick={() => void execute(tool)}
              >
                {renderIcon(tool)}
              </button>
            </TooltipTrigger>
            <TooltipContent>{tool.manifest.name}</TooltipContent>
          </Tooltip>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
    {leading}
    <div className={cn('flex items-center gap-0.5', className)} data-testid={`tid.editor.chromeMount.${mount}`}>
      <TooltipProvider skipDelayDuration={0} disableHoverableContent>
        {inline.map((tool, index) => renderInlineTool(tool, inline[index - 1]))}
        {overflow.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid={`tid.editor.${mount}.overflow`}
                aria-label="More actions"
                className={cn(
                  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg',
                  'text-foreground/80 transition-colors hover:bg-accent hover:text-foreground'
                )}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="bottom" sideOffset={8}>
              {overflow.map((tool) => (
                <DropdownMenuItem
                  key={tool.manifest.id}
                  data-testid={`tid.editor.${mount}.${tool.manifest.id}`}
                  onClick={() => void execute(tool)}
                >
                  <span className="mr-2 inline-flex">{renderIcon(tool)}</span>
                  {tool.manifest.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TooltipProvider>
    </div>
    {trailing}
    </>
  );
};
